<script setup>
import { computed } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { usePortalAccount } from '../../composables/usePortalAccount';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  widgetStyle: {
    type: Object,
    default: () => ({}),
  },
});

const { accountId } = usePortalAccount();

const { data, loading, error } = useApiData(
  computed(() => props.widget),
  computed(() => accountId.value)
);

const title = computed(() => props.widget.title);
const cards = computed(() => {
  // Utiliser les données de l'API si disponibles, sinon les props statiques
  if (data.value?.mapped?.cards && Array.isArray(data.value.mapped.cards)) {
    return data.value.mapped.cards;
  }
  if (data.value?.raw?.cards && Array.isArray(data.value.raw.cards)) {
    return data.value.raw.cards;
  }
  if (data.value?.raw && Array.isArray(data.value.raw)) {
    return data.value.raw;
  }
  return props.widget.properties?.cards || [];
});
</script>

<template>
  <div
    class="h-full flex flex-col font-inter group p-5 bg-white rounded-[20px] shadow-xl border border-slate-100"
  >
    <div v-if="title" class="mb-4 flex items-center justify-between px-1">
      <h3
        class="text-[11px] font-bold text-slate-400 uppercase tracking-[0.1em]"
      >
        {{ title }}
      </h3>
      <div
        class="flex h-6 w-6 items-center justify-center rounded-lg bg-slate-50 border border-slate-100"
      >
        <span class="i-lucide-bar-chart-3 h-3 w-3 text-slate-400" />
      </div>
    </div>
    <div class="flex-1 overflow-auto custom-scrollbar pr-1">
      <div
        v-if="loading && cards.length === 0"
        class="flex items-center justify-center py-8"
      >
        <div class="flex flex-col items-center gap-2">
          <div
            class="h-6 w-6 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin"
          />
          <span
            class="text-[8px] font-bold text-slate-400 uppercase tracking-widest"
            >Loading</span
          >
        </div>
      </div>
      <div v-else class="grid grid-cols-1 gap-4 pb-2">
        <div
          v-for="(card, index) in cards"
          :key="index"
          class="group/card relative overflow-hidden rounded-[20px] border border-slate-100 bg-white p-5 transition-all duration-300 hover:shadow-lg hover:border-slate-200 cursor-pointer"
        >
          <div class="flex items-center justify-between gap-3 relative z-10">
            <div class="flex items-center gap-3.5 flex-1 min-w-0">
              <div
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-300 bg-slate-50 text-slate-500 group-hover/card:bg-indigo-50 group-hover/card:text-indigo-500"
              >
                <span
                  class="text-lg transition-colors duration-300"
                  :class="[card.icon || 'i-lucide-activity']"
                />
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-xs font-bold uppercase tracking-wide text-slate-500 mb-0.5"
                >
                  {{ card.label }}
                </p>
                <p
                  class="text-2xl font-black tracking-tight text-slate-900 leading-none"
                >
                  {{ card.value || '0' }}
                </p>
              </div>
            </div>
            <div v-if="card.trend" class="flex-shrink-0">
              <div
                v-if="card.trend === 'up'"
                class="flex items-center gap-1 text-[10px] font-bold text-emerald-500 px-2 py-1 rounded-md bg-emerald-50"
              >
                <span class="i-lucide-trending-up h-3 w-3" />
                {{ card.trendValue || '' }}
              </div>
              <div
                v-else-if="card.trend === 'down'"
                class="flex items-center gap-1 text-[10px] font-bold text-rose-500 px-2 py-1 rounded-md bg-rose-50"
              >
                <span class="i-lucide-trending-down h-3 w-3" />
                {{ card.trendValue || '' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
